import { Component, Inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { YearlyLicensePurchaseDialogData } from "../../billing.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../../shared/error-state-matcher";

@Component({
  selector: "app-yearly-license-purchase-dialog",
  templateUrl: "./yearly-license-purchase-dialog.component.html",
  styleUrls: ["./yearly-license-purchase-dialog.component.scss"],
  standalone: false,
})
export class YearlyLicensePurchaseDialogComponent {
  public sendingRequest = false;
  public form: FormGroup;
  public calculatedPrice = 0;
  public isAdditionalPurchase = false;
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  constructor(
    public dialogRef: MatDialogRef<YearlyLicensePurchaseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: YearlyLicensePurchaseDialogData,
  ) {
    this.isAdditionalPurchase = !!this.data.lastPaymentDate;
    this.fillInitialForm();
    this.calculatePrice();
  }

  fillInitialForm = () => {
    const isLegacyBilling = this.data.billingMode === "legacy";
    const minValue = this.isAdditionalPurchase ? 0 : isLegacyBilling ? 0 : 10;
    const initialValue = this.isAdditionalPurchase
      ? this.data.defaultEmployeeCount || 0
      : isLegacyBilling
        ? this.data.defaultEmployeeCount || 0
        : Math.max(this.data.defaultEmployeeCount || 0, 10);
    this.form = new FormGroup({
      numberOfEmployees: new FormControl(initialValue, [
        (c) => Validators.required(c),
        Validators.min(minValue),
      ]),
    });

    this.form.get("numberOfEmployees")?.valueChanges.subscribe(() => {
      this.calculatePrice();
    });
  };

  calculatePrice = () => {
    const rawEmployeeCount =
      (this.form.get("numberOfEmployees")?.value as number) || 0;
    const isLegacyBilling = this.data.billingMode === "legacy";
    const employeeCount = this.isAdditionalPurchase
      ? rawEmployeeCount
      : isLegacyBilling
        ? rawEmployeeCount
        : Math.max(rawEmployeeCount, 10);
    const basePrice = this.data.price || 0;
    const fullYearlyPrice = parseFloat(
      (basePrice * employeeCount * 12).toFixed(2),
    );

    if (this.data.lastPaymentDate) {
      const lastPaymentDate = new Date(this.data.lastPaymentDate);
      lastPaymentDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysSinceLastPayment = Math.floor(
        (today.getTime() - lastPaymentDate.getTime()) / (1000 * 60 * 60 * 24),
      );
      const proportionalDiscount = daysSinceLastPayment / 365;
      this.calculatedPrice = Math.max(
        0,
        parseFloat((fullYearlyPrice * (1 - proportionalDiscount)).toFixed(2)),
      );
    } else {
      this.calculatedPrice = fullYearlyPrice;
    }
  };

  submit = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    this.sendingRequest = true;
    const rawEmployeeCount =
      (this.form.get("numberOfEmployees")?.value as number) || 0;
    const isLegacyBilling = this.data.billingMode === "legacy";
    const numberOfEmployees = this.isAdditionalPurchase
      ? rawEmployeeCount
      : isLegacyBilling
        ? rawEmployeeCount
        : Math.max(rawEmployeeCount, 10);
    this.dialogRef.close({ numberOfEmployees, proceed: true });
  };

  close = () => this.dialogRef.close({ proceed: false });
}
